import "../../../css/CrawlSources.css";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Table, Tag, Input, Button, message, Spin, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  LoadingOutlined,
  StopOutlined,
  RedoOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getJob, getJobUrls, stopJob, retryJob } from "../../../services/polly/crawlSourceService";

const { Search } = Input;

/* ── Failure category config ────────────────────────────────────── */

const FAILURE_CATEGORIES = {
  BOT_BLOCKED:   { label: "Bot Blocked",   color: "#cf1322", bg: "#fff1f0" },
  RATE_LIMITED:  { label: "Rate Limited",   color: "#d46b08", bg: "#fff7e6" },
  TIMEOUT:       { label: "Timeout",        color: "#7c3aed", bg: "#f5f3ff" },
  DNS_ERROR:     { label: "DNS Error",      color: "#1d4ed8", bg: "#eff6ff" },
  HTTP_ERROR:    { label: "HTTP Error",     color: "#b91c1c", bg: "#fef2f2" },
  PARSE_ERROR:   { label: "Parse Error",    color: "#92400e", bg: "#fffbeb" },
  OTHER:         { label: "Other",          color: "#6b7280", bg: "#f9fafb" },
};

function categoryOf(url) {
  return url.failureCategory || url.category || "OTHER";
}

/* ── Status pills ───────────────────────────────────────────────── */

const STATUS_CONFIG = {
  COMPLETED:  { color: "green",   label: "Completed" },
  RUNNING:    { color: "blue",    label: "Running" },
  FAILED:     { color: "red",     label: "Failed" },
  STOPPED:    { color: "default", label: "Stopped" },
  QUEUED:     { color: "orange",  label: "Queued" },
};

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || { color: "default", label: status || "Unknown" };
  const isRunning = status === "RUNNING";
  return (
    <Tag color={cfg.color}>
      {isRunning && <LoadingOutlined style={{ marginRight: 4 }} />}
      {cfg.label}
    </Tag>
  );
}

/* ── URL result tag ─────────────────────────────────────────────── */

function ResultTag({ urlRow }) {
  const status = urlRow.fetchStatus || urlRow.status;
  if (status === "INGESTED" || status === "SUCCESS") {
    return <Tag color="green">Ingested{urlRow.httpCode ? ` (${urlRow.httpCode})` : ""}</Tag>;
  }
  if (status === "UNCHANGED") {
    return <Tag color="default">Unchanged</Tag>;
  }
  if (status === "DISCOVERED") {
    return <Tag color="blue">Discovered</Tag>;
  }
  // Failed
  const cat = categoryOf(urlRow);
  const cfg = FAILURE_CATEGORIES[cat] || FAILURE_CATEGORIES.OTHER;
  return (
    <Tag color="red">
      {cfg.label}{urlRow.httpCode ? ` (${urlRow.httpCode})` : ""}
    </Tag>
  );
}

/* ── Filter chip set ────────────────────────────────────────────── */

const FILTER_OPTIONS = [
  { key: "ALL",          label: "All" },
  { key: "INGESTED",     label: "Ingested" },
  { key: "FAILED",       label: "Failed" },
  { key: "BOT_BLOCKED",  label: "Bot Blocked" },
  { key: "RATE_LIMITED",  label: "Rate Limited" },
  { key: "TIMEOUT",      label: "Timeout" },
  { key: "DNS_ERROR",    label: "DNS Error" },
  { key: "HTTP_ERROR",   label: "HTTP Error" },
  { key: "PARSE_ERROR",  label: "Parse Error" },
];

function FilterChips({ active, onChange }) {
  return (
    <div className="job-detail-filter-chips">
      {FILTER_OPTIONS.map((opt) => (
        <span
          key={opt.key}
          className={`job-detail-chip${active === opt.key ? " job-detail-chip-active" : ""}`}
          onClick={() => onChange(opt.key)}
        >
          {opt.label}
        </span>
      ))}
    </div>
  );
}

/* ── Summary banner ─────────────────────────────────────────────── */

function SummaryBanner({ job }) {
  const discovered = job.documentsDiscovered ?? 0;
  const ingested = job.documentsProcessed ?? 0;
  const unchanged = job.documentsUnchanged ?? 0;
  const failed = job.documentsFailed ?? 0;

  const breakdown = job.failureBreakdown || {};

  return (
    <div className="job-detail-summary">
      <div className="job-detail-counters">
        <div className="job-detail-counter">
          <div className="job-detail-counter-value">{discovered}</div>
          <div className="job-detail-counter-label">Discovered</div>
        </div>
        <div className="job-detail-counter">
          <div className="job-detail-counter-value" style={{ color: "#389e0d" }}>{ingested}</div>
          <div className="job-detail-counter-label">Ingested</div>
        </div>
        <div className="job-detail-counter">
          <div className="job-detail-counter-value" style={{ color: "#888" }}>{unchanged}</div>
          <div className="job-detail-counter-label">Unchanged</div>
        </div>
        <div className="job-detail-counter">
          <div className="job-detail-counter-value" style={{ color: failed > 0 ? "#cf1322" : undefined }}>{failed}</div>
          <div className="job-detail-counter-label">Failed</div>
        </div>
      </div>
      {Object.keys(breakdown).length > 0 && (
        <div className="job-detail-breakdown">
          {Object.entries(breakdown).map(([cat, count]) => {
            const cfg = FAILURE_CATEGORIES[cat] || FAILURE_CATEGORIES.OTHER;
            return (
              <Tag key={cat} color={cfg.color} style={{ background: cfg.bg, borderColor: cfg.color }}>
                {cfg.label}: {count}
              </Tag>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Row className helper ───────────────────────────────────────── */

function rowClassName(record) {
  const s = record.fetchStatus || record.status;
  if (s === "INGESTED" || s === "SUCCESS") return "";
  if (s === "UNCHANGED" || s === "DISCOVERED") return "";
  const cat = categoryOf(record);
  const cls = `job-detail-row-${cat.toLowerCase().replace(/_/g, "-")}`;
  return cls;
}

/* ── Main component ─────────────────────────────────────────────── */

export default function JobDetail() {
  const { sourceId, jobId } = useParams();
  const history = useHistory();
  const mountedRef = useRef(true);

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  // URL table state
  const [urls, setUrls] = useState(null); // null = not yet loaded, [] = empty
  const [urlsError, setUrlsError] = useState(false);
  const [urlPage, setUrlPage] = useState(1);
  const [urlPageSize] = useState(20);
  const [urlTotal, setUrlTotal] = useState(0);
  const [urlsLoading, setUrlsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL");

  const [retrying, setRetrying] = useState(false);
  const [stopping, setStopping] = useState(false);

  const searchDebounce = useRef(null);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // Fetch job
  const fetchJob = useCallback(async () => {
    try {
      const data = await getJob(jobId);
      if (!mountedRef.current) return;
      setJob(data);
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn("Failed to load job:", err);
      message.error("Failed to load job details");
    }
    if (mountedRef.current) setLoading(false);
  }, [jobId]);

  // Fetch URLs
  const fetchUrls = useCallback(async (page, search, filter) => {
    setUrlsLoading(true);
    setUrlsError(false);
    try {
      const data = await getJobUrls(jobId, {
        page: page - 1,
        pageSize: urlPageSize,
        search: search || undefined,
        filter: filter !== "ALL" ? filter : undefined,
      });
      if (!mountedRef.current) return;
      const content = Array.isArray(data.content) ? data.content : Array.isArray(data) ? data : [];
      setUrls(content);
      setUrlTotal(data.totalElements ?? content.length);
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn("Failed to load job URLs:", err);
      setUrlsError(true);
      setUrls([]);
    }
    if (mountedRef.current) setUrlsLoading(false);
  }, [jobId, urlPageSize]);

  useEffect(() => { fetchJob(); }, [fetchJob]);
  useEffect(() => { fetchUrls(urlPage, searchText, activeFilter); }, [urlPage, searchText, activeFilter, fetchUrls]);

  const handleSearch = (val) => {
    setSearchText(val);
    setUrlPage(1);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => handleSearch(val), 400);
  };

  const handleFilterChange = (key) => {
    setActiveFilter(key);
    setUrlPage(1);
  };

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopJob(jobId);
      message.success("Job stop requested");
      fetchJob();
    } catch (err) {
      message.error("Failed to stop job");
    }
    if (mountedRef.current) setStopping(false);
  };

  const handleRetryAll = async () => {
    setRetrying(true);
    try {
      await retryJob(jobId, { scope: "all_failed" });
      message.success("Retry initiated for failed URLs");
      fetchJob();
      fetchUrls(urlPage, searchText, activeFilter);
    } catch (err) {
      message.error("Failed to initiate retry");
    }
    if (mountedRef.current) setRetrying(false);
  };

  const handleRetrySingle = async (url) => {
    try {
      await retryJob(jobId, { urls: [url] });
      message.success("Retry initiated");
      fetchUrls(urlPage, searchText, activeFilter);
    } catch (err) {
      message.error("Failed to retry URL");
    }
  };

  if (loading) {
    return (
      <div className="crawl-edit-container" style={{ textAlign: "center", paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="crawl-edit-container" style={{ textAlign: "center", paddingTop: 80, color: "#888" }}>
        Job not found
      </div>
    );
  }

  const isRunning = job.status === "RUNNING";
  const failedCount = job.documentsFailed ?? (job.failureBreakdown
    ? Object.values(job.failureBreakdown || {}).reduce((a, b) => a + b, 0)
    : 0);
  const totalFailed = job.summary?.failed ?? failedCount;
  const jobTitle = job.name || (job.startedAt ? `Crawl ${moment(job.startedAt).format("MMM D, YYYY h:mm A")}` : `Job ${jobId}`);

  const urlColumns = [
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      ellipsis: true,
      render: (url) => (
        <Tooltip title={url}>
          <span style={{ fontSize: 12 }}>{url}</span>
        </Tooltip>
      ),
    },
    {
      title: "Result",
      key: "result",
      width: 160,
      render: (_, record) => <ResultTag urlRow={record} />,
    },
    {
      title: "Duration",
      dataIndex: "durationMs",
      key: "duration",
      width: 100,
      render: (ms) => {
        if (ms == null) return <span style={{ color: "#999" }}>-</span>;
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(1)}s`;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => {
        const s = record.fetchStatus || record.status;
        const isFailed = s === "FAILED" || s === "ERROR"
          || (s !== "INGESTED" && s !== "SUCCESS"
              && s !== "UNCHANGED" && s !== "DISCOVERED");
        if (!isFailed) return null;
        return (
          <Button
            size="small"
            icon={<RedoOutlined />}
            onClick={() => handleRetrySingle(record.url)}
          >
            Retry
          </Button>
        );
      },
    },
  ];

  return (
    <div className="crawl-edit-container">
      <div className="crawl-edit-breadcrumb">
        <a onClick={() => history.push(`/polly/crawl-sources`)}>Crawl Sources</a>
        {" "}&rsaquo;{" "}
        <a onClick={() => history.goBack()}>Past Crawls</a>
        {" "}&rsaquo;{" "}
        <span>Job Detail</span>
      </div>

      <div className="job-detail-body">
        {/* Header */}
        <div className="job-detail-header">
          <div className="job-detail-header-left">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => history.goBack()}
              style={{ marginRight: 8 }}
            />
            <h2 className="job-detail-title">{jobTitle}</h2>
            <StatusPill status={job.status} />
          </div>
          <div className="job-detail-header-right">
            {isRunning ? (
              <Button
                danger
                icon={<StopOutlined />}
                loading={stopping}
                onClick={handleStop}
              >
                Stop
              </Button>
            ) : totalFailed > 0 ? (
              <Button
                type="primary"
                icon={<RedoOutlined />}
                loading={retrying}
                onClick={handleRetryAll}
              >
                Retry {totalFailed} failed URL{totalFailed !== 1 ? "s" : ""}
              </Button>
            ) : null}
          </div>
        </div>

        {/* Summary banner */}
        <SummaryBanner job={job} />

        {/* Search + filters */}
        <div className="job-detail-toolbar">
          <Search
            placeholder="Search URLs..."
            allowClear
            size="small"
            prefix={<SearchOutlined />}
            onSearch={handleSearch}
            onChange={handleSearchChange}
            style={{ maxWidth: 300 }}
          />
        </div>
        <FilterChips active={activeFilter} onChange={handleFilterChange} />

        {/* URL table */}
        {urlsError && urls !== null && urls.length === 0 ? (
          <div className="job-detail-degraded">
            URL details available in next release
          </div>
        ) : (
          <Table
            columns={urlColumns}
            dataSource={urls || []}
            rowKey={(r) => r.url || r.id || Math.random()}
            loading={urlsLoading}
            rowClassName={rowClassName}
            pagination={{
              current: urlPage,
              pageSize: urlPageSize,
              total: urlTotal,
              onChange: (p) => setUrlPage(p),
              showSizeChanger: false,
            }}
            locale={{ emptyText: urlsLoading ? null : "No URLs found" }}
            size="small"
          />
        )}
      </div>
    </div>
  );
}
